import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import { FaRegSquarePlus } from "react-icons/fa6";
import CreateCategory from './CreateCategory'
import * as yup from 'yup';
import { useFormik } from 'formik';
import toast from 'react-hot-toast'
import { createCategory, deleteCategory, editCategory, getCategoryListingPaginate, statusUpdateCategory } from '../../api/context/category'
import imageToBase64 from 'image-to-base64/browser';
import moment from 'moment';
import { BiSolidEditAlt } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import { FiArrowRight, FiPlus } from "react-icons/fi";
import CustomisedModal from '../../components/Modal/CustomisedModal'
import { checkUserPermissions } from '../../helpers/permission'
import Loader from '../../components/loader/loader'
import { Switch } from '@mui/material';

const PAGE_LIMIT = 1000;

const Categories = () => {

    const navigate = useNavigate();

    const [show, setShow] = useState(false);
    const handleShow = () => setShow(true);

    const [loading, setLoading] = useState(false);
    const [update, setUpdate] = useState(false);
    const [category, setCategory] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [modal, setModal] = useState(false);
    const [deletingId, setDeletingId] = useState("");

    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const isVendor = user?.userType === 'Vendor';
    const canManage = checkUserPermissions('manage_category');
    const canViewSubs = checkUserPermissions('view_sub_category');

    const toggleModal = () => setModal(prev => !prev);

    const handleClose = () => {
        formik.resetForm();
        setShow(false);
        setSelectedCategory(null);
    };

    const validationSchema = yup.object().shape({
        title: yup.string('').trim().required('Name is required'),
        desc: yup.string('').trim().required('Description is required'),
        img: selectedCategory?.id ? yup.string().nullable() : yup.string().required('Image is required'),
        isActive: yup.boolean().required('isActive is required'),
    });

    const formik = useFormik({
        initialValues: {
            subCategoryId: '',
            title: '',
            desc: '',
            img: '',
            img_view: '',
            isActive: true
        },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            setLoading(true);
            if (values.img === '') delete values.img;
            delete values.img_view;

            const onDone = (successMsg) => (response) => {
                if (response && response?.data?.statusCode === 200) {
                    resetForm();
                    toast.success(successMsg);
                    handleClose();
                    setUpdate(u => !u);
                    setSelectedCategory(null);
                } else {
                    toast.error(response?.data?.message);
                }
                setLoading(false);
            };

            if (selectedCategory?.id) {
                editCategory(values)(onDone("Category Updated Successfully"));
            } else {
                createCategory(values)(onDone("Category Created Successfully"));
            }
        },
    });

    const handleImageChange = async (e) => {
        const file = e?.target?.files[0];
        const maxSizeInBytes = 2 * 1024 * 1024;

        if (file?.size > maxSizeInBytes) {
            toast.error("File size exceeds 2 MB limit", 'error');
            e.target.value = null;
            return;
        }

        const response = await imageToBase64(URL.createObjectURL(file));
        formik.setValues({
            ...formik.values,
            img: response,
            img_view: 'data:image/jpeg;base64,' + response,
        });
    };

    const getAllCategories = () => {
        setLoading(true);
        getCategoryListingPaginate({ page: 1, limit: PAGE_LIMIT })((response) => {
            if (response?.statusCode === 200) {
                setCategory(response?.data?.subcategories || []);
            } else {
                setCategory([]);
            }
            setLoading(false);
        });
    };

    const handleEditClick = (data) => {
        setSelectedCategory(data);
        formik.setValues({
            ...formik.values,
            subCategoryId: String(data?.id ?? ''),
            title: data?.title ?? '',
            desc: data?.description ?? '',
            img_view: data?.img ?? '',
            isActive: data?.isActive ?? true
        });
        setShow(true);
    };

    const handleConfirm = (id) => {
        setModal(true);
        setDeletingId(id);
    };

    const statusActive = (id) => {
        const previous = [...category];
        setCategory(prev =>
            prev.map(item =>
                item.id === id ? { ...item, isActive: !item.isActive } : item
            )
        );
        statusUpdateCategory(id)((response) => {
            if (response?.data?.statusCode !== 200) {
                setCategory(previous);
                toast.error("Failed to update status");
            }
        });
    };

    const deleteCategories = (id) => {
        deleteCategory(id)((response) => {
            if (response?.data?.statusCode === 200) {
                toast.success("Category deleted successfully");
                setModal(false);
                setUpdate(u => !u);
            } else {
                toast.error(response?.data?.message);
            }
        });
    };

    useEffect(() => {
        getAllCategories();
    }, [update]);

    return (
        <div className="categories-page">
            <style>{`
                /* Match homepage: soft neutral background + consistent 16px horizontal gutter */
                .categories-page { background: #f9fafb; min-height: calc(100vh - 70px); }
                .main-content:has(.categories-page) { background: #f9fafb; }
                .page-content:has(.categories-page) { background: #f9fafb; }
                body:has(.categories-page) .page-content,
                body:has(.categories-page) #layout-wrapper.no-topbar .page-content,
                body:has(.categories-page) #layout-wrapper .page-content {
                    padding-top: 0 !important;
                    padding-bottom: 24px !important;
                    padding-left: 16px !important;
                    padding-right: 16px !important;
                }
                body:has(.categories-page) .container-fluid,
                body:has(.categories-page) .container {
                    padding-left: 0 !important;
                    padding-right: 0 !important;
                }

                /* Sticky header — bleeds edge-to-edge; horizontal padding matches .modern-panel-header */
                .categories-page .page-header-sticky {
                    padding: clamp(14px, 1.6vw, 24px) clamp(14px, 1.4vw, 20px) clamp(10px, 1.2vw, 18px) !important;
                    margin: 0 -16px 16px !important;
                    top: 0 !important;
                    background: #f9fafb !important;
                    border-bottom: 1px solid #eef2f7 !important;
                    box-shadow: none !important;
                }
                @media (max-width: 991px) {
                    .categories-page .page-header-sticky {
                        padding: 16px 16px 12px !important;
                    }
                }
                @media (max-width: 767.98px) {
                    .categories-page .page-header-sticky {
                        padding: 10px 16px 8px !important;
                        margin: 0 -16px 8px !important;
                        position: static !important;
                    }
                }
                @media (max-width: 480px) {
                    .categories-page .page-header-sticky {
                        padding: 10px 16px 8px !important;
                        margin: 0 -16px 8px !important;
                    }
                }

                /* Header */
                .categories-page .page-header-sticky h3 { font-size: 18px !important; font-weight: 700 !important; color: #0f172a !important; letter-spacing: -0.01em !important; margin: 0 0 2px !important; }
                .categories-page .page-header-sticky p { font-size: 12px !important; color: #94a3b8 !important; font-weight: 500 !important; margin: 0 !important; }

                /* Add New button */
                .categories-page .request_btn {
                    background: #008695 !important;
                    border: 1px solid #008695 !important;
                    color: #fff !important;
                    font-weight: 600 !important;
                    padding: 6px 14px !important;
                    border-radius: 4px !important;
                    font-size: 12px !important;
                    height: 34px !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    gap: 6px !important;
                    white-space: nowrap;
                    box-shadow: none !important;
                }
                .categories-page .request_btn:hover,
                .categories-page .request_btn:focus,
                .categories-page .request_btn:active {
                    background: #006672 !important;
                    border-color: #006672 !important;
                    color: #fff !important;
                }

                /* Grid: 4/3/2 columns responsive */
                .categories-page .categories-grid {
                    display: grid !important;
                    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
                    gap: 14px !important;
                    padding: 8px 0 24px !important;
                }
                @media (max-width: 1199.98px) {
                    .categories-page .categories-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; gap: 12px !important; }
                }
                @media (max-width: 767.98px) {
                    .categories-page .categories-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; }
                }

                /* Card */
                .categories-page .category-card {
                    background: #fff !important;
                    border: 1px solid #e2e8f0 !important;
                    border-radius: 8px !important;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.04) !important;
                    overflow: hidden !important;
                    transition: transform 0.15s ease, box-shadow 0.2s ease, border-color 0.15s ease;
                }
                .categories-page .category-card:hover {
                    border-color: #008695 !important;
                    box-shadow: 0 6px 18px -10px rgba(15,23,42,0.14) !important;
                    transform: translateY(-2px);
                }

                /* Card image */
                .categories-page .card-media { aspect-ratio: 4 / 3 !important; }
                .categories-page .card-media .media-placeholder { font-size: 10px !important; color: #94a3b8 !important; }

                /* Edit button on image */
                .categories-page .edit-btn {
                    height: 26px !important;
                    padding: 0 8px !important;
                    border-radius: 4px !important;
                    top: 6px !important;
                    right: 6px !important;
                    gap: 4px !important;
                    font-size: 10px !important;
                    font-weight: 600 !important;
                    background: rgba(255, 255, 255, 0.95) !important;
                    color: #0f172a !important;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.12) !important;
                }
                .categories-page .edit-btn .icon { font-size: 12px !important; }

                /* Card body */
                .categories-page .card-body { gap: 10px !important; padding: 10px 12px 12px !important; }

                /* Title row */
                .categories-page .card-title-row { gap: 8px !important; }
                .categories-page .card-title-row h3 {
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    color: #0f172a !important;
                    line-height: 1.3 !important;
                    letter-spacing: -0.01em !important;
                }

                /* Switch smaller */
                .categories-page .status-inline .MuiSwitch-root { transform: scale(0.85); margin: -4px; }

                /* View sub-categories button */
                .categories-page .view-sub-btn {
                    background: #f8fafc !important;
                    color: #008695 !important;
                    border: 1px solid #e2e8f0 !important;
                    font-size: 11px !important;
                    font-weight: 600 !important;
                    padding: 7px 10px !important;
                    border-radius: 4px !important;
                    gap: 6px !important;
                    box-shadow: none !important;
                    transition: all 0.15s ease;
                }
                .categories-page .view-sub-btn:hover {
                    background: #f0fdfa !important;
                    border-color: #008695 !important;
                }
                .categories-page .view-sub-btn .arrow-icon { font-size: 12px !important; }

                /* Empty state */
                .categories-page .categories-empty {
                    padding: 40px 16px !important;
                    font-size: 12px !important;
                    color: #94a3b8 !important;
                    background: #f8fafc !important;
                    border-radius: 8px !important;
                    border: 1px dashed #cbd5e1 !important;
                }

                /* Mobile fixed submit bar (hidden by default; shown on mobile) */
                .categories-page .mobile-add-bar { display: none; }

                /* Mobile: header stack + slight card tweaks */
                @media (max-width: 767.98px) {
                    .categories-page .page-header-sticky .request_btn { display: none !important; }
                    .categories-page { padding-bottom: 68px !important; }

                    .categories-page .mobile-add-bar {
                        display: flex !important;
                        align-items: center;
                        justify-content: center;
                        position: fixed;
                        left: 0; right: 0; bottom: 0;
                        padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px));
                        background: #fff;
                        border-top: 1px solid #e2e8f0;
                        box-shadow: 0 -4px 12px rgba(15,23,42,0.06);
                        z-index: 1049;
                    }
                    .categories-page .mobile-add-bar .request_btn {
                        width: 100% !important;
                        height: 40px !important;
                        justify-content: center;
                        font-size: 13px !important;
                        padding: 8px 16px !important;
                    }
                }
                @media (max-width: 575.98px) {
                    .categories-page .page-header-sticky { flex-direction: column !important; align-items: flex-start !important; }
                    .categories-page .page-header-sticky h3 { font-size: 16px !important; }
                    .categories-page .card-title-row h3 { font-size: 12px !important; }
                    .categories-page .card-body { padding: 8px 10px 10px !important; gap: 8px !important; }
                    .categories-page .view-sub-btn { font-size: 10px !important; padding: 6px 8px !important; }
                    .categories-page .edit-btn { font-size: 9px !important; height: 24px !important; padding: 0 7px !important; }
                    .categories-page .edit-btn .icon { font-size: 11px !important; }
                }
            `}</style>

            <div className="page-header-sticky d-sm-flex justify-content-between align-items-center flex-wrap gap-2">
                <div style={{ minWidth: 0 }}>
                    <h3 className="fw-bold">
                        {isVendor ? 'Explore Categories' : 'Categories'}
                    </h3>
                </div>

                {canManage && !isVendor && (
                    <Button className="request_btn" onClick={handleShow}>
                        <FiPlus size={14} strokeWidth={2.5} />
                        Add New Category
                    </Button>
                )}
            </div>

            <div className="row">
                <div className="col-12">

                    {loading ? (
                        <Loader />
                    ) : (
                        <div className="categories-grid">
                            {category?.length > 0 ? (
                                category.map((data, index) => (
                                    <div className="category-card" key={data?.id ?? index}>
                                        <div className="card-media">
                                            {data?.img ? (
                                                <img src={data.img} alt={data?.title} />
                                            ) : (
                                                <div className="media-placeholder">No image</div>
                                            )}

                                            {canManage && (
                                                <button
                                                    type="button"
                                                    className="edit-btn"
                                                    onClick={() => handleEditClick(data)}
                                                    title="Edit"
                                                >
                                                    <BiSolidEditAlt className="icon" />
                                                    <span>Edit</span>
                                                </button>
                                            )}
                                        </div>

                                        <div className="card-body">
                                            <div className="card-title-row">
                                                <h3>{data?.title}</h3>
                                                {canManage && (
                                                    <span className="status-inline">
                                                        <Switch
                                                            size="small"
                                                            checked={!!data?.isActive}
                                                            onChange={() => statusActive(data?.id)}
                                                            className="custom-switch"
                                                        />
                                                    </span>
                                                )}
                                            </div>

                                            {canViewSubs && (
                                                <button
                                                    type="button"
                                                    className="view-sub-btn"
                                                    onClick={() => navigate('/sub-categories/' + data?.id, { state: { data } })}
                                                >
                                                    <span>View Sub Categories</span>
                                                    <FiArrowRight className="arrow-icon" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="categories-empty">
                                    No categories found
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <CreateCategory
                    show={show}
                    handleClose={handleClose}
                    handleShow={handleShow}
                    formik={formik}
                    handleImageChange={handleImageChange}
                    selectedCategory={selectedCategory}
                    loading={loading}
                />

                <CustomisedModal
                    isOpen={modal}
                    customClass={"policy_modal"}
                    toggle={toggleModal}
                >
                    <h5>Are you sure you want to remove this item?</h5>
                    <div className="policy_Agree">
                        <Button
                            onClick={() => setModal(false)}
                            size="lg"
                            className="btn_cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="lg"
                            className="btn"
                            onClick={() => deleteCategories(deletingId)}
                        >
                            Remove
                        </Button>
                    </div>
                </CustomisedModal>
            </div>

            {/* Mobile-only fixed Add New Category button at the bottom */}
            {canManage && !isVendor && (
                <div className="mobile-add-bar">
                    <Button className="request_btn" onClick={handleShow}>
                        <FiPlus size={14} strokeWidth={2.5} />
                        Add New Category
                    </Button>
                </div>
            )}
        </div>
    );
};

export default Categories;
